'use client';

import { useState, useCallback } from 'react';
import { Quote } from '@/util/types/serverTypes';
import { useCheckExistingQuotes } from './useCheckExistingQuotes';

/**
 * Centralized hook for fetching and caching draft/abandoned quotes
 * 
 * Provides a single source of truth for quote fetching that can be shared
 * between ResumeDraftBanner and ContactForm. Quotes are cached in component
 * state to prevent unnecessary refetches.
 * 
 * @param jwtToken - JWT token for authentication
 * @param companyId - Company ID for the request
 * @returns Object with quotes, loading state, error, fetchQuotes function, and clearQuotes function
 */
export function useDraftQuotes(jwtToken: string, companyId: string) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { checkQuotes } = useCheckExistingQuotes(jwtToken, companyId);

  const fetchQuotes = useCallback(
    async (contactId: string) => {
      if (!contactId) {
        setQuotes([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await checkQuotes(contactId);
        setQuotes(result || []);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err : new Error('Failed to fetch quotes');
        setError(errorMessage);
        setQuotes([]);
      } finally {
        setIsLoading(false);
      }
    },
    [checkQuotes]
  );

  const clearQuotes = useCallback(() => {
    setQuotes([]);
    setError(null);
  }, []);

  return { quotes, isLoading, error, fetchQuotes, clearQuotes };
}

